import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Clock, FileImage, Shirt, Gift, Check, X, ChevronDown } from "lucide-react";
import { useBusinessContact, WaBusinessLink } from "@/lib/whatsapp";

export const buildSections = (s) => [
  {
    id: "turnaround", icon: Clock, title: "Turnaround & rush", items: [
      { q: "How long does an order take?", a: `Standard turnaround is ${s.turnaround_days} day${s.turnaround_days === 1 ? "" : "s"} for up to ${s.turnaround_max_shirts} shirts, counted from the moment you approve the artwork.` },
      { q: `I need more than ${s.turnaround_max_shirts} shirts.`, a: s.large_order_text },
      { q: "Can I get it next day?", a: s.rush_text, badge: s.rush_available ? "Available this week" : "Not this week" },
    ],
  },
  {
    id: "artwork", icon: FileImage, title: "Artwork & files", items: [
      { q: "What files do you accept?", a: s.files_text },
      { q: "I only have a screenshot.", a: s.screenshots_text },
      { q: "Will you print without checking with me?", a: s.approval_text },
    ],
    link: { to: "/artwork-guidelines", label: "See the full Artwork Guidelines" },
  },
];

const ICONS = { 0: Shirt, 1: Gift };

const Item = ({ q, a, id, badge }) => (
  <details className="group border-b border-zinc-800 last:border-0" data-testid={`faq-item-${id}`}>
    <summary className="flex items-center justify-between gap-3 py-3.5 cursor-pointer list-none text-sm font-semibold text-white">
      <span className="flex items-center gap-2 flex-wrap">{q}{badge && <span data-testid="rush-badge" className={`text-[10px] font-mono-p uppercase tracking-wider px-2 py-0.5 rounded-full ${badge.startsWith("Available") ? "bg-[#4fd23a]/15 text-[#4fd23a]" : "bg-zinc-800 text-zinc-400"}`}>{badge}</span>}</span><ChevronDown size={16} className="text-zinc-500 shrink-0 transition-transform group-open:rotate-180" />
    </summary>
    <p className="text-sm text-zinc-400 leading-relaxed pb-4">{a}</p>
  </details>
);

export default function FAQ() {
  const contact = useBusinessContact();
  const { data: s } = useQuery({ queryKey: ["public-faq"], queryFn: async () => (await api.get("/public/faq")).data, staleTime: 60000 });
  if (!s) return <div className="max-w-4xl mx-auto px-4 py-12 text-zinc-500 font-mono-p">Loading…</div>;
  const sections = buildSections(s);
  const services = { yes: s.services_yes || [], no: s.services_no || [] };
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="faq-page">
      <p className="text-xs font-mono-p uppercase tracking-wider text-[#22c3d6]">Good to know</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mt-2">FAQ</h1>
      <p className="text-zinc-400 mt-3 max-w-2xl">Turnaround times, what files to send, and exactly what we do (and don't) print.</p>

      {sections.map((s) => (
        <section key={s.id} className="mt-10 bg-[#12141a] border border-zinc-800 rounded-2xl p-5 sm:p-6" data-testid={`faq-section-${s.id}`}>
          <h2 className="font-display text-base md:text-lg font-bold text-white flex items-center gap-2"><s.icon size={18} className="text-[#ff6a13]" />{s.title}</h2>
          <div className="mt-2">{s.items.map((it, i) => <Item key={i} id={`${s.id}-${i}`} {...it} />)}</div>
          {s.link && <Link to={s.link.to} data-testid={`faq-link-${s.id}`} className="inline-block mt-2 text-sm text-[#22c3d6] hover:text-white hover:underline underline-offset-2">{s.link.label} →</Link>}
        </section>
      ))}

      <section className="mt-10" data-testid="faq-services">
        <h2 className="font-display text-base md:text-lg font-bold text-white">Our services</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {services.yes.map((sv, i) => { const Ic = ICONS[i] || Shirt; return (
            <div key={sv.title} className="rounded-2xl border border-[#4fd23a]/40 bg-[#12141a] p-5" data-testid={`service-yes-${sv.title.split(" ")[0].toLowerCase()}`}>
              <div className="flex items-center gap-2"><span className="inline-flex w-6 h-6 rounded-full bg-[#4fd23a]/15 text-[#4fd23a] items-center justify-center"><Check size={14} strokeWidth={3} /></span><Ic size={18} className="text-[#22c3d6]" /><p className="font-display font-bold text-white">{sv.title}</p></div>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{sv.body}</p>
            </div>
          ); })}
          {services.no.map((sv) => (
            <div key={sv.title} className="rounded-2xl border border-[#ff2fb0]/40 bg-[#ff2fb0]/5 p-5 md:col-span-2" data-testid={`service-no-${sv.title.split(" ")[0].toLowerCase()}`}>
              <div className="flex items-center gap-2"><span className="inline-flex w-6 h-6 rounded-full bg-[#ff2fb0]/15 text-[#ff2fb0] items-center justify-center"><X size={14} strokeWidth={3} /></span><p className="font-display font-bold text-white">{sv.title}</p></div>
              <p className="text-sm text-zinc-300 mt-2 leading-relaxed">{sv.body}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-12 flex flex-col sm:flex-row gap-3" data-testid="faq-cta">
        <WaBusinessLink num={contact.whatsapp_number} text="Hi! I have a question about an order." data-testid="faq-whatsapp-btn" className="inline-flex items-center justify-center h-11 px-5 rounded-full bg-[#25D366] hover:bg-[#3ae07b] text-black font-semibold text-sm transition-colors">Still have a question? WhatsApp us</WaBusinessLink>
        <Link to="/builder" data-testid="faq-start-btn" className="inline-flex items-center justify-center h-11 px-5 rounded-full bg-[#ff6a13] hover:bg-[#ff8a3d] text-white font-semibold text-sm transition-colors">Start an order</Link>
      </div>
    </div>
  );
}
